import {
  IInsightQueryLayout,
  IInsightQueryCondition,
  IInsightFieldTypes,
  ISqlCondition,
  IInsightSqlConditionOperators,
  ConditionalOperator,
  IAggregationField,
  SqlFunction,
  SupportedDataType,
} from "@gtmhub/insights/models/models";

class SqlQueryBuilder {
  private query = "";

  public select(clause?: string): SqlQueryBuilder {
    this.query = `${this.query} SELECT ${clause ? clause : ""}`;
    return this;
  }

  public from(clause: string): SqlQueryBuilder {
    this.query = `${this.query} FROM ${clause}`;
    return this;
  }

  public where(clause?: string): SqlQueryBuilder {
    this.query = `${this.query} WHERE ${clause ? clause : ""}`;
    return this;
  }

  public bracket(flag: "open" | "close"): SqlQueryBuilder {
    if (flag === "open") {
      this.query = ` ${this.query} (`;
    } else if (flag === "close") {
      this.query = `${this.query})`;
    }

    return this;
  }

  public conditionOperator(conditionalOperator: ConditionalOperator): SqlQueryBuilder {
    this.query = `${this.query} ${conditionalOperator || ""}`;
    return this;
  }

  public getQuery(): string {
    return this.query;
  }

  public condition(condition: string): SqlQueryBuilder {
    this.query = `${this.query} ${condition || ""}`;
    return this;
  }
}
export class QueryBuilderService {
  private fieldTypes = this.getFieldTypes();
  private conditionTemplateFuncMap = {
    equals: (c: ISqlCondition) => {
      if (this.isArray(c.dataType)) {
        return `${c.field} = ${this.arrayWrapper(c.value)}`;
      } else if (c.dataType === this.fieldTypes.string || this.isDate(c.dataType)) {
        return `${c.field} = ${this.stringWrapper(c.value)}`;
      }

      return `${c.field} = ${c.value}`;
    },
    notEqual: (c: ISqlCondition) => {
      if (this.isArray(c.dataType)) {
        return `${c.field} != ${this.arrayWrapper(c.value)}`;
      } else if (c.dataType === this.fieldTypes.string || this.isDate(c.dataType)) {
        return `${c.field} != ${this.stringWrapper(c.value)}`;
      }
      return `${c.field} != ${c.value}`;
    },
    contains: (c: ISqlCondition) => {
      if (this.isArray(c.dataType)) {
        return `${c.field} @> ${this.arrayWrapper(c.value)}`;
      }

      return `${c.field} LIKE '%${c.value}%'`;
    },
    doesNotContain: (c: ISqlCondition) => {
      if (this.isArray(c.dataType)) {
        return `NOT ${c.field} @> ${this.arrayWrapper(c.value)}`;
      }

      return `${c.field} NOT LIKE '%${c.value}%'`;
    },
    isContainedBy: (c: ISqlCondition) => {
      return `${c.field} <@ ${this.arrayWrapper(c.value)}`;
    },
    isNotContainedBy: (c: ISqlCondition) => {
      return `NOT ${c.field} <@ ${this.arrayWrapper(c.value)}`;
    },
    overlaps: (c: ISqlCondition) => {
      return `${c.field} && ${this.arrayWrapper(c.value)}`;
    },
    doesNotOverlap: (c: ISqlCondition) => {
      return `NOT ${c.field} && ${this.arrayWrapper(c.value)}`;
    },
    hasLengthOf: (c: ISqlCondition) => {
      return `CARDINALITY(${c.field}) = ${c.value}`;
    },
    greater: (c: ISqlCondition) => {
      if (this.isDate(c.dataType)) {
        return `${c.field} > ${this.stringWrapper(c.value)}`;
      }

      return `${c.field} > ${c.value}`;
    },
    less: (c: ISqlCondition) => {
      if (this.isDate(c.dataType)) {
        return `${c.field} < ${this.stringWrapper(c.value)}`;
      }

      return `${c.field} < ${c.value}`;
    },
    longer: (c: ISqlCondition) => {
      if (this.isArray(c.dataType)) {
        return `CARDINALITY(${c.field}) > ${c.value}`;
      }
      return `LENGTH(${c.field}) > ${c.value}`;
    },
    shorter: (c: ISqlCondition) => {
      if (this.isArray(c.dataType)) {
        return `CARDINALITY(${c.field}) < ${c.value}`;
      }
      return `LENGTH(${c.field}) < ${c.value}`;
    },
  };

  public generateSqlScript(sqlConditions: ISqlCondition[], sqlOperator: ConditionalOperator, sqlQuery: IInsightQueryLayout): string {
    sqlQuery.conditions = this.buildConditionsTree(sqlConditions, sqlOperator);
    return this.buildSqlQuery(sqlQuery);
  }

  public flattenConditions(
    condition: IInsightQueryCondition,
    sqlQueryOperators: IInsightSqlConditionOperators[],
    flattenConditions: ISqlCondition[] = []
  ): ISqlCondition[] {
    if (!condition) {
      return flattenConditions;
    }

    if (this.isNumber(condition.dataType)) {
      condition.value = +condition.value;
    }

    if (this.isDate(condition.dataType)) {
      condition.value = this.addTimezoneOffset(condition.value);
    }

    flattenConditions.push({
      value: condition.value,
      dataType: condition.dataType,
      strOperator: condition.strOperator,
      title: condition.title,
      field: condition.field,
      sqlOperators: this.getConditionSqlOperators(condition.dataType, sqlQueryOperators),
      isSingleValue: condition.isSingleValue,
    });

    return this.flattenConditions(condition.nextCondition, sqlQueryOperators, flattenConditions);
  }

  public getConditionSqlOperators = (dataType: SupportedDataType, sqlOperators: IInsightSqlConditionOperators[]): string[] => {
    for (const sqlOperator of sqlOperators) {
      if (sqlOperator.fieldType === dataType) {
        return sqlOperator.operators;
      }
    }

    return [];
  };

  public getFieldTypes(): IInsightFieldTypes {
    return {
      integer: "integer",
      long: "long",
      double: "double",
      date: "date",
      string: "string",
      timestamp: "timestamp",
      bool: "bool",
      dateTime: "dateTime",
      unixTime: "unixTime",
      arrayStrings: "[]string",
    };
  }

  public isNumber(dataType: string): boolean {
    return dataType === this.fieldTypes.integer || dataType === this.fieldTypes.long || dataType === this.fieldTypes.double;
  }

  public isArray(dataType: string): boolean {
    return dataType === this.fieldTypes.arrayStrings;
  }

  public isDate(dataType: string): boolean {
    return dataType === this.fieldTypes.date || dataType === this.fieldTypes.timestamp || dataType === this.fieldTypes.dateTime || dataType === this.fieldTypes.unixTime;
  }

  private getConditionTemplateFunc(operator: string): (c: ISqlCondition) => string {
    return this.conditionTemplateFuncMap[operator];
  }

  private buildConditionsTree(conditions: ISqlCondition[], conditionalOperator: ConditionalOperator): IInsightQueryCondition {
    let conditionsTree: IInsightQueryCondition;

    (conditions || []).forEach((c, idx) => {
      const enrichedCondition: IInsightQueryCondition = {
        title: c.title,
        dataType: c.dataType,
        field: c.field,
        value: c.value,
        templateFunc: this.getConditionTemplateFunc(c.strOperator),
        nextConditionBooleanLogic: idx === conditions.length - 1 ? null : conditionalOperator,
        bluredValue: c.value,
        strOperator: c.strOperator,
        isSingleValue: c.isSingleValue,
      };

      if (idx === 0) {
        // root condition
        conditionsTree = enrichedCondition;
      } else {
        this.addCondition(conditionsTree, enrichedCondition);
      }
    });

    return conditionsTree;
  }

  // assign condition as a nextCondition to the innermost condition in the conditions tree
  private addCondition(conditionsTree: IInsightQueryCondition, condition: IInsightQueryCondition): void {
    if (!conditionsTree.nextCondition) {
      conditionsTree.nextCondition = condition;
      return;
    }
    this.addCondition(conditionsTree.nextCondition, condition);
  }

  private buildSqlQuery(sqlQueryObject: IInsightQueryLayout): string {
    const builder = new SqlQueryBuilder();
    const selectExpr = this.getSelectExpression(sqlQueryObject.sqlFunction, sqlQueryObject.aggregatedField);
    builder.select(selectExpr).from(sqlQueryObject.entity);

    if (sqlQueryObject.conditions) {
      builder.where();
      // Note: sqlQueryObject.conditions contains the root condition (if any)
      // all other conditions are nested within nextCondition property
      this.extractConditions(sqlQueryObject.conditions, builder);
    }

    // remove extra spaces
    return builder.getQuery().replace(/\s\s+/g, " ");
  }

  private getSelectExpression(selectFunc: SqlFunction, aggregatedField: IAggregationField): string {
    return selectFunc === SqlFunction.Count ? "count(*)" : `${selectFunc}(${aggregatedField.name})`;
  }

  private stringWrapper(value: string): string {
    return `'${value}'`;
  }

  private arrayWrapper(array: string): string {
    return `'{${array}}'`;
  }

  private subtractTimezoneOffset(value: string): Date {
    const date = new Date(value);
    const tzoffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzoffset);
  }

  private addTimezoneOffset(value: string): Date {
    const date = new Date(value);
    const tzoffset = new Date().getTimezoneOffset() * 60000;
    return new Date(date.getTime() + tzoffset);
  }

  private containsMultipleChoices(condition: IInsightQueryCondition): boolean {
    // delimiter is comma and a single whitespace character
    return typeof condition.value === this.fieldTypes.string && condition.value.match(/,\s{1}/);
  }

  private isConditionalOperatorNegative(operator: string): boolean {
    const negativeOperators = ["notEqual", "doesNotContain", "isNotContainedBy", "doesNotOverlap"];
    return negativeOperators.includes(operator);
  }

  private generateMultiClauseCondition(condition: IInsightQueryCondition, builder: SqlQueryBuilder): void {
    const noBracketsAndNewLine = condition.value.replace(/[[\]\n\r]+/g, "");

    // delimiter is comma and a single whitespace character
    const splitConditions: string[] = noBracketsAndNewLine.split(/,\s{1}/);

    if (splitConditions.length === 1) {
      builder.condition(condition.templateFunc(condition));
      return;
    }

    splitConditions.forEach((value: string, idx: number) => {
      const operator = this.isConditionalOperatorNegative(condition.strOperator) ? "AND" : "OR";

      const c = {
        field: condition.field,
        value: value,
        dataType: condition.dataType,
      };

      if (idx === 0) {
        builder.bracket("open").condition(condition.templateFunc(c)).conditionOperator(operator);
      } else {
        if (idx === splitConditions.length - 1) {
          builder.condition(condition.templateFunc(c)).bracket("close").conditionOperator(condition.nextConditionBooleanLogic);
        } else {
          builder.condition(condition.templateFunc(c)).conditionOperator(operator);
        }
      }
    });
  }

  private extractConditions(condition: IInsightQueryCondition, builder: SqlQueryBuilder): void {
    if (!condition) {
      return;
    }

    if (condition.isSingleValue === false || this.containsMultipleChoices(condition)) {
      this.generateMultiClauseCondition(condition, builder);
    } else {
      if (this.isDate(condition.dataType)) {
        condition.value = this.subtractTimezoneOffset(condition.value).toISOString();
      }

      builder.condition(condition.templateFunc(condition)).conditionOperator(condition.nextConditionBooleanLogic);
    }

    this.extractConditions(condition.nextCondition, builder);
  }
}
